import { useRef, useState } from 'react';
import { ArrowRightOnRectangleIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../lib/contexts/AuthContext';
import { logout as logoutAPI } from '../../lib/api/auth';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import AccountIdentity from './AccountIdentity';
import ThemeControl from './ThemeControl';
import SendFeedbackDialog from './SendFeedbackDialog';

/**
 * Story 24.5 — the account popover at the right of the app header
 * (DESIGN.md › AvatarMenu). Tenant, name, one role badge, the Theme control
 * (Story 25.8), Send feedback (Story 36.3), then Sign out.
 * The identity block is `AccountIdentity`, shared with the phone More page
 * (Story 24.7).
 *
 * - A non-modal Radix Popover, so the content is `role="dialog"` named
 *   "Account" — deliberately NOT an ARIA menu: it holds a text block, the
 *   Theme radiogroup and two buttons, not a list of commands.
 * - Theme (Story 25.8): `ThemeControl`, Light · Dark · System, between the
 *   identity block and Sign out. The popover is `w-72` (288px) so the three
 *   segments fit as a radiogroup; the `<select>` labelled "Theme" is only the
 *   SegmentedControl's below-400px / overflow fallback.
 * - Send feedback (Story 36.3): its own section between Theme and Sign out.
 *   The popover is controlled so the click can close it and open
 *   `SendFeedbackDialog`, which renders as a sibling of the content so it
 *   outlives the popover. Closing the dialog returns focus to the trigger.
 * - Focus on close: Radix's non-modal Popover skips the trigger refocus after
 *   any outside interaction, so the rule is done here by hand. On close the
 *   trigger takes focus UNLESS something other than `<body>` already has it:
 *   Radix runs `onCloseAutoFocus` after the browser's own mousedown focus, so
 *   a control the person clicked keeps focus, while Esc or a click on plain
 *   content (focus on body) returns it to the avatar.
 */

/** Two initials from the first two words, else the first two letters. */
function getUserInitials(name: string | undefined): string {
  const names = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (names.length === 0) return 'U';
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  return names[0].substring(0, 2).toUpperCase();
}

export default function AvatarMenu() {
  const { user } = useAuth();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Story 37.1 — CSP img-src blocks external avatar hosts; fall back to the
  // initials on error. Tracks WHICH url failed so a new url gets a fresh try.
  const [failedAvatar, setFailedAvatar] = useState<string | null>(null);

  const handleLogout = async () => {
    // The API logout blacklists the token and redirects.
    await logoutAPI();
  };

  return (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <Button ref={triggerRef} variant="ghost" size="icon" aria-label="Account" className="rounded-full">
          {user?.avatar_url && failedAvatar !== user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt=""
              className="size-8 rounded-full object-cover"
              loading="lazy"
              onError={() => setFailedAvatar(user.avatar_url ?? null)}
            />
          ) : (
            <span
              aria-hidden="true"
              className="flex size-8 items-center justify-center rounded-full bg-agent-3 text-agent-3-ink type-caption"
            >
              {getUserInitials(user?.name)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        aria-label="Account"
        align="end"
        className="w-72 p-0 bg-surface-raised border-line-floating rounded-lg shadow-float"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          const active = document.activeElement;
          if (!active || active === document.body) triggerRef.current?.focus();
        }}
      >
        <AccountIdentity className="px-4 py-3" />
        <div className="border-t border-line px-2 py-2">
          <ThemeControl />
        </div>
        <div className="border-t border-line p-1">
          <Button
            variant="ghost"
            onClick={() => {
              setMenuOpen(false);
              setFeedbackOpen(true);
            }}
            className="w-full justify-start hover:bg-hover-on-raised"
          >
            <ChatBubbleLeftEllipsisIcon aria-hidden="true" />
            Send feedback
          </Button>
        </div>
        <div className="border-t border-line p-1">
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start hover:bg-hover-on-raised"
          >
            <ArrowRightOnRectangleIcon aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </PopoverContent>
      <SendFeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} returnFocusRef={triggerRef} />
    </Popover>
  );
}
